"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";

/**
 * Agency Master Downline Export — progress context.
 *
 * Mirrors the lead-import / route-optimization pattern so the agency owner
 * can kick off a big export, leave the page, come back, and still see the
 * widget tracking progress.
 *
 * Lifecycle:
 *   1. MasterExport.tsx calls startJob() with the new job id returned from
 *      POST /agencies/master-export/jobs.
 *   2. The context starts a 2s polling loop on GET /jobs/:id, updating the
 *      job state in-memory.
 *   3. When status flips to 'completed', we fetch a fresh signed download
 *      URL from GET /jobs/:id/download and trigger the browser download.
 *   4. The widget then sticks around for ~15s of "Done" celebration so the
 *      user notices, then auto-dismisses.
 *   5. On page load, listRecent() pulls any in-flight jobs the user
 *      started in a prior session so the widget rehydrates.
 *
 * Persistence: server-side. The widget can come and go but the job lives
 * in export_jobs until completion (and the file lives in Supabase Storage
 * for 7 days after that).
 */

export type ExportJobStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'canceled';

export interface ExportJob {
  id: string;
  status: ExportJobStatus;
  format: 'csv' | 'xlsx';
  totalRows: number | null;
  processedRows: number;
  progressMessage: string | null;
  rowCount: number | null;
  fileSizeBytes: number | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  hasFile: boolean;
  /** Big exports are split into several files (each under the storage cap). */
  partCount?: number;
  /** Cached download URL once we fetch it (signed; short-lived). */
  downloadUrl?: string;
  /** Local flag so we only auto-download once per completion. */
  autoDownloaded?: boolean;
}

interface ExportProgressContextValue {
  activeJobs: ExportJob[];
  /** Adds a freshly-created job to the polling loop (called by MasterExport). */
  startJob: (jobId: string) => void;
  /** Manually dismiss a finished/failed job from the widget. */
  dismissJob: (jobId: string) => void;
  /** Cancel a still-running job. */
  cancelJob: (jobId: string) => Promise<void>;
  /** Force-trigger the download for a completed job (used by the widget button). */
  triggerDownload: (jobId: string) => Promise<void>;
}

const ExportProgressContext = createContext<ExportProgressContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_DURATION_MS = 30 * 60 * 1000; // 30 min watchdog per job
const AUTO_DISMISS_DELAY_MS = 15_000;

export const ExportProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState<ExportJob[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Polling state keyed by jobId. We keep timers in a ref so re-renders
  // don't restart them.
  const pollersRef = useRef<Map<string, { interval: ReturnType<typeof setInterval>; startedAt: number }>>(new Map());
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Rehydrate: on mount, list the user's recent jobs and adopt any that are
  // still queued/processing. Means an owner who refreshed mid-export sees
  // the widget pick back up automatically.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get({
          url: getApiUrl('/agencies/master-export/jobs'),
          auth: true,
        });
        if (cancelled) return;
        const rows: any[] = (res as any)?.data?.data?.jobs ?? [];
        const inFlight = rows.filter((j) => j.status === 'queued' || j.status === 'processing');
        for (const row of inFlight) {
          startJob(row.id);
        }
      } catch {
        // Silent — rehydration is nice-to-have.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop all timers on unmount.
  useEffect(() => {
    return () => {
      pollersRef.current.forEach((p) => clearInterval(p.interval));
      pollersRef.current.clear();
      dismissTimersRef.current.forEach((t) => clearTimeout(t));
      dismissTimersRef.current.clear();
    };
  }, []);

  const stopPolling = useCallback((jobId: string) => {
    const entry = pollersRef.current.get(jobId);
    if (entry) {
      clearInterval(entry.interval);
      pollersRef.current.delete(jobId);
    }
  }, []);

  const scheduleAutoDismiss = useCallback((jobId: string) => {
    const existing = dismissTimersRef.current.get(jobId);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
      dismissTimersRef.current.delete(jobId);
    }, AUTO_DISMISS_DELAY_MS);
    dismissTimersRef.current.set(jobId, t);
  }, []);

  const triggerDownload = useCallback(async (jobId: string) => {
    try {
      // A split export is several files: fetch and save each part in turn.
      let partCount = 1;
      for (let part = 1; part <= partCount; part++) {
        const res = await api.get({
          url: getApiUrl(`/agencies/master-export/jobs/${jobId}/download${part > 1 ? `?part=${part}` : ''}`),
          auth: true,
        });
        const data = (res as any)?.data?.data;
        const url = data?.url;
        if (!url) {
          toast({ title: 'Could not generate download link', variant: 'destructive' });
          return;
        }
        partCount = data?.partCount || 1;
        if (part === 1) {
          // Cache the URL on the job so the widget can show the button enabled.
          setActiveJobs((prev) =>
            prev.map((j) => (j.id === jobId ? { ...j, downloadUrl: url, autoDownloaded: true } : j)),
          );
        }
        const a = document.createElement('a');
        a.href = url;
        a.download = data?.filename || `master-downline-export.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Browsers drop downloads fired back to back.
        if (part < partCount) await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err: any) {
      toast({
        title: 'Download failed',
        description: err?.message || 'The file may have expired.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const poll = useCallback(async (jobId: string) => {
    try {
      const res = await api.get({
        url: getApiUrl(`/agencies/master-export/jobs/${jobId}`),
        auth: true,
      });
      const job: ExportJob = (res as any)?.data?.data?.job;
      if (!job) return;

      setActiveJobs((prev) => {
        const existing = prev.find((j) => j.id === jobId);
        // Preserve client-only fields like downloadUrl / autoDownloaded
        const merged = { ...(existing ?? {}), ...job } as ExportJob;
        if (existing) {
          return prev.map((j) => (j.id === jobId ? merged : j));
        }
        return [merged, ...prev];
      });

      if (job.status === 'completed') {
        stopPolling(jobId);
        // Auto-trigger the download once. The user can re-trigger via the
        // widget button if they dismissed too quickly.
        const already = activeJobs.find((j) => j.id === jobId)?.autoDownloaded;
        if (!already) {
          await triggerDownload(jobId);
        }
        // Invalidate any cached export-related views.
        queryClient.invalidateQueries({ queryKey: ['exportJobs'] });
        scheduleAutoDismiss(jobId);
      } else if (job.status === 'failed' || job.status === 'canceled') {
        stopPolling(jobId);
        scheduleAutoDismiss(jobId);
      }
    } catch (err: any) {
      // 404 means the server lost the row (shouldn't happen) — stop polling.
      if (err?.response?.status === 404) {
        stopPolling(jobId);
        setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
      }
      // Other errors are transient — keep polling.
    }
  }, [activeJobs, queryClient, scheduleAutoDismiss, stopPolling, triggerDownload]);

  const startJob = useCallback((jobId: string) => {
    if (pollersRef.current.has(jobId)) return;
    // Insert a placeholder row so the widget renders immediately.
    setActiveJobs((prev) => {
      if (prev.some((j) => j.id === jobId)) return prev;
      const placeholder: ExportJob = {
        id: jobId,
        status: 'queued',
        format: 'csv',
        totalRows: null,
        processedRows: 0,
        progressMessage: 'Queued…',
        rowCount: null,
        fileSizeBytes: null,
        errorMessage: null,
        createdAt: new Date().toISOString(),
        startedAt: null,
        completedAt: null,
        expiresAt: null,
        hasFile: false,
      };
      return [placeholder, ...prev];
    });
    // Kick off polling + watchdog.
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        stopPolling(jobId);
        return;
      }
      poll(jobId);
    }, POLL_INTERVAL_MS);
    pollersRef.current.set(jobId, { interval, startedAt });
    // Immediate first poll so we don't wait 2s for the first state read.
    poll(jobId);
  }, [poll, stopPolling]);

  const dismissJob = useCallback((jobId: string) => {
    stopPolling(jobId);
    const t = dismissTimersRef.current.get(jobId);
    if (t) {
      clearTimeout(t);
      dismissTimersRef.current.delete(jobId);
    }
    setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, [stopPolling]);

  const cancelJob = useCallback(async (jobId: string) => {
    try {
      await api.delete({
        url: getApiUrl(`/agencies/master-export/jobs/${jobId}`),
        auth: true,
      });
      // The next poll will pick up status='canceled' and stop.
      toast({ title: 'Export canceled' });
    } catch (err: any) {
      toast({ title: 'Could not cancel', description: err?.message || 'Try again', variant: 'destructive' });
    }
  }, [toast]);

  return (
    <ExportProgressContext.Provider
      value={{ activeJobs, startJob, dismissJob, cancelJob, triggerDownload }}
    >
      {children}
    </ExportProgressContext.Provider>
  );
};

export const useExportProgress = (): ExportProgressContextValue => {
  const ctx = useContext(ExportProgressContext);
  if (!ctx) {
    // Safe no-op fallback so callers don't have to gate on provider presence.
    return {
      activeJobs: [],
      startJob: () => {},
      dismissJob: () => {},
      cancelJob: async () => {},
      triggerDownload: async () => {},
    };
  }
  return ctx;
};
